import { unwrapResult } from '@reduxjs/toolkit';
import { storySlice } from './storyApi';
import { NODE_TYPES } from '../../constants/flowGraph';
import { VARIABLE_TYPES } from '../../constants/main';
import i18n from 'i18next';

/**
 * Update the variables list by appending any local variables to the fetched ones
 * @param {string} orgId The organization ID
 * @param {string} tenantId The tenant ID
 * @param {Array} nodes The list of nodes in the flow graph
 * @param {string} activityId The ID of the associated chaptersource/activity
 */
export const updateStoryVariablesWithLocalNodes = (orgId, tenantId, nodes, activityId) => async (dispatch) => {
  const extractLocalVariables = (nodes) => {
    const localQuestionNodes = nodes.filter(element => [NODE_TYPES.SELECT, NODE_TYPES.FILE_UPLOAD, NODE_TYPES.INPUT_QUERY].includes(element.type) && element.data.questionId);
    const localCallbackNodes = nodes.filter(element => element.type === NODE_TYPES.SEND_STORED_DATA && element.data.sendStoredData?.callbackId);
    const localVariableNodes = nodes.filter(element => element.type === NODE_TYPES.VARIABLE && element.data.variable?.key);
    const localSemanticSearchNodes = nodes.filter(element => element.type === NODE_TYPES.SEMANTIC_SEARCH && element.data.variableName);
    const localBodyMappingsHttpNodes = nodes.filter(element => element.type === NODE_TYPES.HTTP_REQUEST && element.data.httpRequestData?.mappings?.some(mapping => mapping.variable));
    const localResponseStatuscodeHttpNodes = nodes.filter(element => element.type === NODE_TYPES.HTTP_REQUEST && element.data.httpRequestData?.responseStatusCodeMapping);
    const localResponseHeaderMappingsHttpNodes = nodes.filter(element => element.type === NODE_TYPES.HTTP_REQUEST && element.data.httpRequestData?.responseHeaderMappings?.some(mapping => mapping.variable));
    const localLlmNodes = nodes.filter(element => element.type === NODE_TYPES.LLM && (element.data?.variable || element?.data?.llm?.variable));
    const localGenerationNodes = nodes.filter(element => element.type === NODE_TYPES.GENERATION && (element.data?.variable || element?.data?.generationParams?.variable));
    const localCSATNodes = nodes.filter(element => element.type === NODE_TYPES.CSAT && element.data?.variableName);
    const localVariables = []
      .concat(localVariableNodes.map(node => ({
        name: node.data.variable.key,
        type: node.data.variable.type,
        description: node.data.variable.description,
        ...(activityId && { fromActivity: activityId })
      }))) // Add the locally created variables to the list of variables
      .concat(localQuestionNodes.map(node => ({
        name: node.data.questionId,
        type: VARIABLE_TYPES.QUESTION,
        ...(activityId && { fromActivity: activityId })
      }))) // Add any yet unsaved question nodes to the list of variables
      .concat(localCallbackNodes.map(node => ({
        name: node.data.sendStoredData.callbackId,
        type: VARIABLE_TYPES.CALLBACK,
        ...(activityId && { fromActivity: activityId })
      }))) // Add any yet unsaved callback nodes to the list of variables
      .concat(localBodyMappingsHttpNodes.flatMap(node => node.data.httpRequestData.mappings.filter(mapping => mapping.variable).map(mapping => ({
        name: mapping.variable,
        type: mapping.type,
        ...(activityId && { fromActivity: activityId })
      })))) // Add all mappings from http nodes to the list of variables
      .concat(localResponseStatuscodeHttpNodes.map(node => ({
        name: node.data.httpRequestData.responseStatusCodeMapping,
        type: VARIABLE_TYPES.SESSION,
        ...(activityId && { fromActivity: activityId })
      }))) // Add any yet unsaved question nodes to the list of variables
      .concat(localResponseHeaderMappingsHttpNodes.flatMap(node => node.data.httpRequestData.responseHeaderMappings.filter(mapping => mapping.variable).map(mapping => ({
        name: mapping.variable,
        type: mapping.type || VARIABLE_TYPES.SESSION,
        ...(activityId && { fromActivity: activityId })
      })))) // Add all mappings from http nodes to the list of variables
      .concat(localLlmNodes.map(node => ({
        name: node?.data?.variable?.key || node?.data?.llm?.variable,
        type: VARIABLE_TYPES.SESSION,
        ...(activityId && { fromActivity: activityId })
      }))) // Add all mappings from llm nodes to the list of variables
      .concat(localGenerationNodes.map(node => ({
        name: node?.data?.variable?.key || node?.data?.generationParams?.variable,
        type: VARIABLE_TYPES.SESSION,
        ...(activityId && { fromActivity: activityId })
      }))) // Add all mappings from generation nodes to the list of variables
      .concat(localCSATNodes.flatMap(node => [{ name: `${node.data.variableName}.avg`, type: VARIABLE_TYPES.CSAT, description: i18n.t(`Average score`), ...(activityId && { fromActivity: activityId }) },
        ...node.data.sections.map(section => ({
          name: `${node.data.variableName}.${section.id}`, type: VARIABLE_TYPES.CSAT, description: `${section.title} ${i18n.t('score')}`, ...(activityId && { fromActivity: activityId })
        }))])) // Add all mappings from csat nodes to the list of variables
      .concat(localSemanticSearchNodes.map(node => ({
        name: node?.data?.variableName,
        type: VARIABLE_TYPES.SESSION,
        ...(activityId && { fromActivity: activityId })
      })));

    return localVariables;
  };

  const mergeAndSortVariables = (variables, localVariables) => {
    const newVariablesMap = [...variables, ...localVariables].reduce((acc, variable) => {
      if (acc[variable.name]) {
        acc[variable.name] = { ...acc[variable.name], ...variable };
      } else {
        acc[variable.name] = variable;
      }
      return acc;
    }, {});

    const updatedVariables = Object.values(newVariablesMap)
      .filter((item, index, self) => self.findIndex(t => t.name === item.name) === index) // Keep only unique values
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically

    return updatedVariables;
  };

  const localVariables = extractLocalVariables(nodes);

  // Restrict unnecessary api call if there are no local variables to merge
  if (localVariables.length > 0 && orgId && tenantId) {
    // Fetch the latest data
    dispatch(storySlice.endpoints.fetchStoryVariables.initiate({ orgId, tenantId }))
      .then(unwrapResult) // Unwrap the result of the first dispatch
      .then(() => {
        // Update the store with the updated data
        dispatch(storySlice.util.updateQueryData('fetchStoryVariables', { orgId, tenantId }, (variables) => {

          // Combine fetched and local variables, remove duplicates, and sort alphabetically
          const updatedVariables = mergeAndSortVariables(variables, localVariables);

          return updatedVariables;
        }));
      });
  }
};
